/**
 * Dedicated logger for content loading and build processes.
 * Respects CONTENT_LOAD_LOG environment variable.
 */

export function logContent(message: string, level: "info" | "warn" | "error" = "info"): void {
  const isEnabled = process.env.CONTENT_LOAD_LOG !== undefined && process.env.CONTENT_LOAD_LOG !== "0";

  if (!isEnabled) return;

  const prefix = "[content]";
  const formattedMessage = `${prefix} ${message}`;

  switch (level) {
    case "error":
      console.error(formattedMessage);
      break;
    case "warn":
      console.warn(formattedMessage);
      break;
    default:
      console.log(formattedMessage);
  }
}

export function logWarn(message: string): void {
  logContent(message, "warn");
}

export function logError(message: string): void {
  logContent(message, "error");
}

export function logInfo(message: string): void {
  logContent(message, "info");
}

// ---------------------------------------------------------------------------
// App-level logger for debugging (auth, API, subscription). No PII.
// Enabled: NODE_ENV=development, or NEXT_PUBLIC_DEBUG=1, or sessionStorage.fairhelm_debug=1
// ---------------------------------------------------------------------------

const PREFIX = "[FairHelm]";

function isAppDebugEnabled(): boolean {
  if (typeof process !== "undefined" && process.env.NODE_ENV === "development") return true;
  if (typeof process !== "undefined" && process.env.NEXT_PUBLIC_DEBUG === "1") return true;
  if (typeof window !== "undefined") {
    try {
      return sessionStorage.getItem("fairhelm_debug") === "1";
    } catch {
      return false;
    }
  }
  return false;
}

function formatPayload(data?: Record<string, unknown>): string {
  if (data == null || Object.keys(data).length === 0) return "";
  try {
    return " " + JSON.stringify(data);
  } catch {
    return "";
  }
}

export const appLog = {
  debug(message: string, data?: Record<string, unknown>): void {
    if (!isAppDebugEnabled()) return;
    console.debug(PREFIX, message, formatPayload(data));
  },
  info(message: string, data?: Record<string, unknown>): void {
    if (!isAppDebugEnabled()) return;
    console.info(PREFIX, message, formatPayload(data));
  },
  warn(message: string, data?: Record<string, unknown>): void {
    if (!isAppDebugEnabled()) return;
    console.warn(PREFIX, message, formatPayload(data));
  },
  error(message: string, data?: Record<string, unknown>): void {
    if (!isAppDebugEnabled()) return;
    console.error(PREFIX, message, formatPayload(data));
  },
};

/** Log backend API call (url, method, status, duration_ms). Call from fetch wrappers or after fetch. */
export function appLogApi(
  method: string,
  url: string,
  status: number,
  durationMs: number,
  extra?: Record<string, unknown>
): void {
  if (!isAppDebugEnabled()) return;
  const msg = `API ${method} ${url} ${status} ${durationMs}ms`;
  appLog.debug(msg, extra);
}
